import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { rootRoute } from './__root';
import {
  auditLogsListQueryOptions,
  auditLogKeys,
} from '@/features/audit-logs/queries';
import { auditLogsSearchSchema } from '@/features/audit-logs/search-schema';
import { searchToAuditParams } from '@/features/audit-logs/audit-log-helpers';
import { AuditLogsPendingState } from '@/features/audit-logs/AuditLogsPendingState';
import { getAuditLogFilters } from '@/lib/audit-logs';
import { ACCESS } from '@/features/access/access';
import { requireRoleAccess } from '@/features/access/role-route-guard';
import { primeListQuery } from '@/lib/route-loader';

export const auditLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit-logs',
  validateSearch: auditLogsSearchSchema,
  beforeLoad: requireRoleAccess(ACCESS.audit),
  // Re-run the loader when the user navigates between filter/page/sort variants.
  loaderDeps: ({ search }) => ({ params: searchToAuditParams(search) }),
  // Prime the list + filter-options caches before the component mounts. Swallow
  // loader errors — the page renders its own loading/error/forbidden/empty
  // states (a 403 for non-admins must not short-circuit the route render).
  //
  // The list prime only *blocks* (awaits) on a genuinely cold cache. Once any
  // page of this list has been fetched, `loaderDeps` changing again (a new
  // filter/sort/page combo) must not re-trigger the full-page
  // `pendingComponent` — `AuditLogsPage`'s own `isFetching` + confined
  // `TableRefetchOverlay` owns that transition. See `primeListQuery`.
  loader: ({ context: { queryClient }, deps: { params } }) => {
    void queryClient
      .ensureQueryData({
        queryKey: auditLogKeys.filters(),
        queryFn: () => getAuditLogFilters(),
      })
      .catch(() => undefined);
    return primeListQuery(
      queryClient,
      auditLogKeys.lists(),
      auditLogsListQueryOptions(params),
    );
  },
  // `pendingMs: 0` / `pendingMinMs: 0` — no debounce, no artificial display
  // floor (see `routes/email-accounts.$id.tsx` for the precedent/rationale).
  pendingComponent: AuditLogsPendingState,
  pendingMs: 0,
  pendingMinMs: 0,
  component: lazyRouteComponent(
    () => import('@/features/audit-logs/AuditLogsPage'),
    'AuditLogsPage',
  ),
});
